/**
 * Client API for materials catalog snapshot / restore callable.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

export type MaterialsCatalogSnapshotMeta = {
  id: string;
  label: string;
  note: string;
  createdAt: string | null;
  createdByUid: string;
  createdByEmail: string;
  timberCount: number;
  hardwareCount: number;
  storagePath: string;
  schemaVersion: number;
  byteSize: number;
  lastRestoredAt: string | null;
  lastRestoredByUid: string | null;
  lastRestoredByEmail: string | null;
};

type CreateResult = { ok: true; snapshot: MaterialsCatalogSnapshotMeta };
type ListResult = { ok: true; snapshots: MaterialsCatalogSnapshotMeta[] };
type GetResult = { ok: true; snapshot: MaterialsCatalogSnapshotMeta };
type RestoreResult = {
  ok: true;
  snapshot: MaterialsCatalogSnapshotMeta;
  timberWritten: number;
  timberDeleted: number;
  hardwareWritten: number;
  hardwareDeleted: number;
};

function callable() {
  return httpsCallable(getFunctions(undefined, 'us-central1'), 'materialsCatalogSnapshot');
}

export async function createMaterialsCatalogSnapshot(
  label: string,
  note = '',
): Promise<MaterialsCatalogSnapshotMeta> {
  const res = await callable()({ action: 'create', label, note });
  return (res.data as CreateResult).snapshot;
}

export async function listMaterialsCatalogSnapshots(): Promise<MaterialsCatalogSnapshotMeta[]> {
  const res = await callable()({ action: 'list' });
  return (res.data as ListResult).snapshots || [];
}

export async function getMaterialsCatalogSnapshot(
  snapshotId: string,
): Promise<MaterialsCatalogSnapshotMeta> {
  const res = await callable()({ action: 'get', snapshotId });
  return (res.data as GetResult).snapshot;
}

/** Destructive: replaces live timber + hardware catalogs from the snapshot. confirm must be "RESTORE". */
export async function restoreMaterialsCatalogSnapshot(
  snapshotId: string,
  confirm: string,
): Promise<RestoreResult> {
  const res = await callable()({ action: 'restore', snapshotId, confirm });
  return res.data as RestoreResult;
}
